import type { FC, ComponentType } from 'react';
import React, { forwardRef, useEffect, useCallback } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import CrossIcon from '@atlaskit/icon/core/cross';
import Button from '@atlaskit/button/standard-button';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import EmojiIcon from '@atlaskit/icon/core/emoji';
import RefreshIcon from '@atlaskit/icon/core/refresh';
import type { PopupComponentProps } from '@atlaskit/popup/types';
import Spinner from '@atlaskit/spinner/spinner';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { withSessionData } from '@confluence/session-data/entry-points/withSessionData';
import type { SessionDataContextInjected } from '@confluence/session-data/entry-points/withSessionData';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { NTH_USER_EMOJI_TITLE_DISCOVERED } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';

import type { EmojiTitleComponentPopoverProps } from './__types__/EmojiTitle';
import { ConfluenceGraphQLDefaultTitleEmoji as DefaultTitleEmojiValues } from './__types__/EmojiTitleButtonUpdateDefaultTitleEmojiMutation';

const i18n = defineMessages({
	removeEmoji: {
		id: 'emoji-title.remove-emoji',
		description:
			'Text in the button to prompt user to remove emoji from editor title when they hover over the title in editor.',
		defaultMessage: 'Remove emoji',
	},
	emojiLabel: {
		id: 'emoji-title.add-emoji.floating-toolbar',
		description:
			'Text in the button to prompt user to add emoji to editor title when they hover over the title in editor and button is located on the floating toolbar.',
		defaultMessage: 'Emoji',
	},
	restoreEmoji: {
		id: 'emoji-title.restore-emoji',
		description:
			'Text in the button to prompt user to restore emoji to editor title if emoji has been removed.',
		defaultMessage: 'Restore emoji',
	},
});

const PopupComponent = forwardRef<HTMLDivElement, PopupComponentProps>((props, ref) => {
	const { style, ...otherProps } = props;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
	return <div {...otherProps} ref={ref} style={{ ...style }} />;
});

const EmojiTitlePopoverComponent: FC<
	EmojiTitleComponentPopoverProps & SessionDataContextInjected
> = ({
	contentId,
	hasEmoji,
	onRemoveEmoji,
	isLoading,
	emojiPicker,
	setOpenSecondEmojiPicker,
	setIsEmojiButtonFocused,
	openSecondEmojiPicker,
	onEmojiPickerLoaded,
	userOffline,
	floatingToolbarButtonStyle,
	updateDefaultEmoji,
	hasLiveDocsDefaultEmoji,
	autoPageEmojiCohort,
	emojiTitleDiscovered,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isLivePage = useIsCurrentPageLive();
	const { setOnboardingState } = useSetOnboardingState();
	const isInRandomEmojiCohort = autoPageEmojiCohort && autoPageEmojiCohort === 'random';

	useEffect(() => {
		onEmojiPickerLoaded && onEmojiPickerLoaded();
	}, [onEmojiPickerLoaded]);

	const setEmojiTitleDiscoveredOnboardingState = () => {
		if (isInRandomEmojiCohort && !emojiTitleDiscovered) {
			setOnboardingState({
				key: NTH_USER_EMOJI_TITLE_DISCOVERED.EMOJI_TITLE_DISCOVERED,
				value: 'true',
			});
		}
	};

	const sendAnalyticsEvent = ({
		element,
		action,
		actionSubject,
		actionSubjectId,
		attributes = {},
	}: {
		element: string;
		action: string;
		actionSubject: string;
		actionSubjectId: string;
		attributes?: Record<string, string | null | undefined>;
	}) => {
		if (createAnalyticsEvent) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action,
					actionSubject,
					actionSubjectId,
					source: 'EmojiTitlePopover',
					attributes: {
						contentId,
						element,
						isLivePage,
						...attributes,
					},
				},
			}).fire();
		}
	};
	const handleFocus = useCallback(() => {
		setIsEmojiButtonFocused(true);
	}, [setIsEmojiButtonFocused]);

	const handleBlur = useCallback(() => {
		setIsEmojiButtonFocused(false);
	}, [setIsEmojiButtonFocused]);

	const openSecondEmojiPickerWithAnalytics = () => {
		// analytic event for clicking on button
		sendAnalyticsEvent({
			element: 'button',
			action: 'clicked',
			actionSubject: 'addEmojiButton',
			actionSubjectId: 'EmojiTitlePopover-addEmoji',
		});

		setOpenSecondEmojiPicker(!openSecondEmojiPicker);

		// analytic event for opening the emoji picker
		sendAnalyticsEvent({
			element: 'button',
			action: 'opened',
			actionSubject: 'emojiPicker',
			actionSubjectId: 'emojiPicker_opened',
			attributes: {
				triggerType: 'addEmojiButton, changeEmojiButton',
				inputMethod: 'title',
			},
		});
	};

	const restoreDefaultEmoji = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'restoreEmojiButton',
				actionSubjectId: 'restoreEmojiButton_clicked',
				source: 'EmojiTitlePopover',
				attributes: {
					contentId,
					isLivePage,
				},
			},
		}).fire();

		void updateDefaultEmoji({ defaultTitleEmoji: DefaultTitleEmojiValues.LIVE_PAGE_DEFAULT });
	};

	return (
		<Popup
			data-testid="emoji-popup"
			isOpen={openSecondEmojiPicker}
			autoFocus={false}
			popupComponent={PopupComponent as ComponentType<PopupComponentProps>}
			onClose={() => {}}
			content={() => emojiPicker}
			trigger={(triggerProps) => (
				<div
					data-testid="emoji-popup-button"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className="emoji-popup-button"
					onFocus={handleFocus}
					onBlur={handleBlur}
				>
					{hasEmoji || hasLiveDocsDefaultEmoji ? (
						// @ts-expect-error
						<Button
							{...triggerProps}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							style={floatingToolbarButtonStyle || {}}
							appearance="subtle"
							data-testid="emoji-remove-button"
							id="emoji-popup-button"
							isDisabled={isLoading || userOffline}
							spacing="compact"
							onClick={(event) => {
								onRemoveEmoji(event);

								setEmojiTitleDiscoveredOnboardingState();

								sendAnalyticsEvent({
									element: 'button',
									action: 'clicked',
									actionSubject: 'removeEmojiButton',
									actionSubjectId: 'EmojiTitlePopover-removeEmoji',
								});
							}}
							iconBefore={
								isLoading ? (
									<Spinner size="small" />
								) : (
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									<span className="emoji-icon">
										<CrossIcon label={intl.formatMessage(i18n.removeEmoji)} size="small" />
									</span>
								)
							}
						>
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
							<span className="emoji-popup-button-text">
								{intl.formatMessage(i18n.removeEmoji)}
							</span>
						</Button>
					) : (
						// @ts-expect-error
						<Button
							{...triggerProps}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							style={floatingToolbarButtonStyle || {}}
							appearance="subtle"
							data-testid="emoji-add-button"
							id="emoji-popup-button"
							isDisabled={isLoading || userOffline}
							spacing="compact"
							isSelected={openSecondEmojiPicker}
							onClick={() => {
								isLivePage ? void restoreDefaultEmoji() : openSecondEmojiPickerWithAnalytics();
								setEmojiTitleDiscoveredOnboardingState();
							}}
							iconBefore={
								isLoading ? (
									<Spinner size="small" />
								) : (
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									<span className="emoji-icon">
										{isLivePage ? (
											<RefreshIcon
												label={intl.formatMessage(i18n.restoreEmoji)}
												testId="restore-icon"
											/>
										) : (
											<EmojiIcon
												label={intl.formatMessage(i18n.emojiLabel)}
												testId="add-emoji-icon"
											/>
										)}
									</span>
								)
							}
						>
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
							<span className="emoji-popup-button-text">
								{isLivePage
									? intl.formatMessage(i18n.restoreEmoji)
									: intl.formatMessage(i18n.emojiLabel)}
							</span>
						</Button>
					)}
				</div>
			)}
			placement="bottom-start"
			// match other title popups
			zIndex={700}
		/>
	);
};

export const EmojiTitlePopover = withSessionData(EmojiTitlePopoverComponent);
EmojiTitlePopover.displayName = 'EmojiTitlePopover';
